// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { createLinkIconStyles } from "@rc/App/SharedStyles/sharedStyles";
import ShareEntityButton, { ShareEntityButtonStyles } from "@rc/Overrides/Components/ShareEntityButton";

import { xScreen } from "@rc/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {};

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;
export interface InvoiceDetailsButtonSetStyles {
    wrapper?: InjectableCss;
    printLink?: LinkPresentationProps;
    fileButton?: LinkPresentationProps;

    shareEntityButtonStyles?: ShareEntityButtonStyles;
}

export const invoiceDetailsButtonSetStyles: InvoiceDetailsButtonSetStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin: 12px 0;
            gap: 32px;
            ${xScreen("lte", "md")} {
                gap: 12px;
            }
            @media print {
                display: none;
            }
        `,
    },
    printLink: { ...createLinkIconStyles("PrintInactive", undefined, false) },
    fileButton: {
        color: "common.black",
        typographyProps: {
            size: "15px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "IconFile",
                size: 15,
                css: css`
                    margin-right: 8px;
                `,
            },
        },
    },
};

const styles = invoiceDetailsButtonSetStyles;

const InvoiceDetailsButtonSet: React.FC<Props> = ({ fields }) => {
    const { value: invoice } = React.useContext(InvoiceStateContext);

    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;

    if (!invoice) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ShareEntityButton
                entityId={invoice.invoiceNumber}
                variant="link"
                entityName="Invoice"
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
            <Link {...styles.printLink} onClick={openPrintDialog} data-test-selector="invoiceDetails_print">
                {translate("Print")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(InvoiceDetailsButtonSet),
    definition: {
        allowedContexts: ["InvoiceDetailsPage"],
        displayName: "Invoice Details Button Set",
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
